import React, { Fragment, FunctionComponent, useMemo } from 'react';
import { memberId } from '@trello/session-cookie';
import { featureFlagClient } from '@trello/feature-flag-client';
import {
  getRouteIdFromPathname,
  isBoardRoute,
  getBoardShortLinkFromPathname,
  isCardRoute,
  getCardShortLinkFromPathname,
  isMemberHomeRoute,
} from '@trello/routes';
import { getLocation } from '@trello/router';

import { useBoardRoutePreloaderQuery } from './BoardRoutePreloaderQuery.generated';
import { useCardRoutePreloaderQuery } from './CardRoutePreloaderQuery.generated';
import { useMemberHomeRoutePreloaderQuery } from './MemberHomeRoutePreloaderQuery.generated';
import { isShortLink } from '@trello/shortlinks';

const cachedFlagValues: {
  [key: string]: boolean;
} = {};
function cachedFlagValue(key: string, defaultValue: boolean) {
  if (cachedFlagValues[key] === undefined) {
    cachedFlagValues[key] = featureFlagClient.get(key, defaultValue);
  }
  return cachedFlagValues[key];
}

export const RoutePreloader: FunctionComponent = ({ children }) => {
  const isBoardRoutePreloadingEnabled = cachedFlagValue(
    'fep.board_route_preloading',
    false,
  );
  const isCardRoutePreloadingEnabled = cachedFlagValue(
    'fep.card_route_preloading',
    false,
  );

  const isMemberHomeRoutePreloadingEnabled = cachedFlagValue(
    'fep.member_home_route_preloading',
    false,
  );

  // We memoize the result here with a empty dependency array so that it is
  // only called once for the initial page load, and then not for transitions.
  const {
    boardShortLink,
    cardShortLink,
    isBoard,
    isCard,
    isMemberHome,
  } = useMemo(() => {
    const { pathname } = getLocation();
    const routeId = getRouteIdFromPathname(pathname);
    const isBoard = isBoardRoute(routeId);
    const isCard = isCardRoute(routeId);
    const isMemberHome = isMemberHomeRoute(routeId);

    return {
      isBoard,
      isCard,
      isMemberHome,
      boardShortLink: isBoard ? getBoardShortLinkFromPathname(pathname) : '',
      cardShortLink: isCard ? getCardShortLinkFromPathname(pathname) : '',
    };
  }, []);

  const { loading: boardLoading } = useBoardRoutePreloaderQuery({
    variables: {
      boardId: boardShortLink || '',
      memberId: memberId || '',
    },
    skip:
      !memberId ||
      !isBoard ||
      !isBoardRoutePreloadingEnabled ||
      !boardShortLink ||
      !isShortLink(boardShortLink),
    // There are some situations where the server isn't returning consistent data from the rest APIs. This means that
    // during state changes to this component we can get a cache miss and re-fire the preload request. By switching
    // to 'network-only' then 'cache-only' we hydrate large portions of the cache, but avoid the risk of firing the
    // network request multiple times.
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const { loading: cardLoading } = useCardRoutePreloaderQuery({
    variables: {
      cardId: cardShortLink || '',
      memberId: memberId || '',
    },
    skip:
      !memberId ||
      !isCard ||
      !isCardRoutePreloadingEnabled ||
      !cardShortLink ||
      !isShortLink(cardShortLink),
    // There are some situations where the server isn't returning consistent data from the rest APIs. This means that
    // during state changes to this component we can get a cache miss and re-fire the preload request. By switching
    // to 'network-only' then 'cache-only' we hydrate large portions of the cache, but avoid the risk of firing the
    // network request multiple times.
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const { loading: memberHomeLoading } = useMemberHomeRoutePreloaderQuery({
    variables: {
      memberId: memberId || '',
    },
    skip: !memberId || !isMemberHome || !isMemberHomeRoutePreloadingEnabled,
    // There are some situations where the server isn't returning consistent data from the rest APIs. This means that
    // during state changes to this component we can get a cache miss and re-fire the preload request. By switching
    // to 'network-only' then 'cache-only' we hydrate large portions of the cache, but avoid the risk of firing the
    // network request multiple times.
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  return cardLoading || boardLoading || memberHomeLoading ? null : (
    <Fragment>{children}</Fragment>
  );
};
